import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import en from './locales/en.json';
import zh from './locales/zh.json';

export type Lang = 'en' | 'zh';

type Dict = { [key: string]: string | Dict };

const dictionaries: Record<Lang, Dict> = {
  en: en as Dict,
  zh: zh as Dict,
};

const STORAGE_KEY = 'botianhome.lang';
const WIPE_DURATION_MS = 500;
const WIPE_FLIP_AT_MS = 250;

function readStoredLang(): Lang {
  if (typeof localStorage === 'undefined') return 'en';
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'zh' || raw === 'en' ? raw : 'en';
}

function lookup(dict: Dict, key: string): string | undefined {
  const parts = key.split('.');
  let cur: string | Dict | undefined = dict;
  for (const part of parts) {
    if (typeof cur !== 'object' || cur === null) return undefined;
    cur = (cur as Dict)[part];
  }
  return typeof cur === 'string' ? cur : undefined;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    name in vars ? String(vars[name]) : `{${name}}`,
  );
}

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LangContext = createContext<LangContextValue | null>(null);

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readStoredLang());
  const [transitioning, setTransitioning] = useState(false);
  const transitioningRef = useRef(false);

  const setLang = useCallback(
    (next: Lang) => {
      if (transitioningRef.current) return;
      if (next === lang) return;
      transitioningRef.current = true;
      setTransitioning(true);
      window.setTimeout(() => {
        setLangState(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // ignore — private mode etc.
        }
      }, WIPE_FLIP_AT_MS);
      window.setTimeout(() => {
        transitioningRef.current = false;
        setTransitioning(false);
      }, WIPE_DURATION_MS);
    },
    [lang],
  );

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const primary = lookup(dictionaries[lang], key);
      if (primary !== undefined) return interpolate(primary, vars);
      const fallback = lookup(dictionaries.en, key);
      if (fallback !== undefined) return interpolate(fallback, vars);
      return key;
    },
    [lang],
  );

  const value = useMemo<LangContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  const overlay = transitioning
    ? createElement(
        'div',
        {
          className: 'fixed inset-0 z-50 pointer-events-none overflow-hidden',
          'aria-hidden': true,
        },
        createElement('div', { className: 'h-full w-full bg-neutral-950 animate-wipe' }),
      )
    : null;

  return createElement(LangContext.Provider, { value }, children, overlay);
}

export function useLang(): { lang: Lang; setLang: (lang: Lang) => void } {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside <LangProvider>');
  return { lang: ctx.lang, setLang: ctx.setLang };
}

export function useT(): (key: string, vars?: Record<string, string | number>) => string {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useT must be used inside <LangProvider>');
  return ctx.t;
}

export function pickLocalized(value: { en: string; zh: string }, lang: Lang): string {
  return value[lang] || value.en;
}
